import './kitStyles';
import React, { useEffect, useRef } from 'react';
import { layerAll, layerBase, layerMode, layerRoot } from './layerScope';

/**
 * WEB-3310 юнит 2б-1 — туман лент и мягкие края прокрутки (эталон
 * `design/cabinet-rescin/kit.jsx:972–992`, кит 02:67–78; владелец 24.10):
 *  - лента из списка `HS`, которая прокручивается сама (`overflow-x` не
 *    `visible`/`clip` и остаток больше 1), получает `data-fog` и `--fl`/`--fr` —
 *    туман с той стороны, где ещё есть что листать (до 28px); не прокручивается —
 *    всё снимается;
 *  - тело открытого окна (`dialog.modal[open] > .mb`) даёт окну `--fto`/`--fbo`
 *    (0…1 за 16px) — мягкая граница под шапкой и над кнопками;
 *  - шапка из списка `ED` внутри прокрутки или соседка прокрутки получает
 *    `data-edge` и `--eto`.
 * Пересчёт: `scroll` в фазе захвата, смена размера окна, изменения дерева и
 * атрибута `open` — раз в кадр и второй замер через 450 мс (после раскладки и
 * шрифтов). Списки `HS` и `ED` — дословно из эталона (в них классы экранов:
 * страницы рескина получат туман без своего кода).
 * Ставится один раз рядом с `TipLayer` и `TouchRules`: владельцем корня `.ds` внутри
 * корня (витрина `/kit`) — наблюдение и обработчики судят только свой корень; либо
 * оболочкой кабинета вне `.ds` с `everyRoot` — наблюдатель на `document.body`, судятся
 * узлы с любым ближайшим `.ds` (правило — `layerScope.ts`). Узлы без `.ds` (страницы
 * на antd) не задеваются; снимается при размонтировании.
 */
const HS =
  '.c2-tabs, .an-lens, .seg, .c-chips, .an-f, .od-f, .hx-hr, .an-mt, .an-tchips, .an-fl, .od-tabs, .od-tbar, .sk-tabs, .sk-ml, .mc-lens, .mc-tr, .v2-hd .r, .v2-gal, .c2-st, .am-kpi, .bp-g, .ak-psh, .bx-pw, .ab2-cmp';
const ED = '.sto-h, .ws-top, .insp-h, .bp-gh, .v2-sum.open > .v2-grab';
const MB = 'dialog.modal[open] > .mb';
const F = 28;

const frac = (v: number) => Math.min(1, v / 16).toFixed(2);

function setH(el: HTMLElement): void {
  const ox = getComputedStyle(el).overflowX;
  const max = ox === 'visible' || ox === 'clip' ? 0 : el.scrollWidth - el.clientWidth;
  if (max <= 1) {
    if (el.hasAttribute('data-fog')) {
      el.removeAttribute('data-fog');
      el.style.removeProperty('--fl');
      el.style.removeProperty('--fr');
    }
    return;
  }
  el.setAttribute('data-fog', '');
  el.style.setProperty('--fl', Math.round(Math.min(F, Math.max(0, el.scrollLeft))) + 'px');
  el.style.setProperty('--fr', Math.round(Math.min(F, Math.max(0, max - el.scrollLeft))) + 'px');
}

function setV(mb: HTMLElement): void {
  const d = mb.closest('dialog');
  if (!d) return;
  const max = mb.scrollHeight - mb.clientHeight;
  d.style.setProperty('--fto', max > 1 ? frac(mb.scrollTop) : '0');
  d.style.setProperty('--fbo', max > 1 ? frac(max - mb.scrollTop) : '0');
}

export function FogRules({ everyRoot = false }: { everyRoot?: boolean } = {}): React.ReactElement {
  const mark = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const mode = layerMode(mark.current, everyRoot);
    if (!mode) return undefined;
    const all = (sel: string) => layerAll(mode, sel);
    const setE = (top: Element) => {
      for (const h of all(ED)) {
        if (!(top.contains(h) || (h.parentElement && h.parentElement.contains(top)))) continue;
        if (h.contains(top)) continue;
        h.setAttribute('data-edge', '');
        h.style.setProperty('--eto', frac(top.scrollTop));
      }
    };
    let raf = 0;
    let tmo: ReturnType<typeof setTimeout> | undefined;
    const scan = () => {
      raf = 0;
      all(HS).forEach(setH);
      all(MB).forEach(setV);
      all(ED).forEach((h) => h.setAttribute('data-edge', ''));
    };
    const later = () => {
      if (!raf) raf = requestAnimationFrame(scan);
      clearTimeout(tmo);
      tmo = setTimeout(scan, 450);
    };
    const onScroll = (e: Event) => {
      const t = e.target;
      if (t === document) {
        const top = document.scrollingElement || document.documentElement;
        if (top) setE(top);
        return;
      }
      if (!(t instanceof HTMLElement) || !layerRoot(t, mode)) return;
      if (t.matches(HS)) setH(t);
      else if (t.matches('dialog.modal > .mb')) setV(t);
      else setE(t);
    };
    let alive = true;
    document.fonts?.ready?.then(() => alive && later());
    const mo = new MutationObserver(later);
    mo.observe(layerBase(mode), { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    window.addEventListener('load', later);
    window.addEventListener('resize', later);
    document.addEventListener('scroll', onScroll, true);
    later();
    return () => {
      alive = false;
      mo.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(tmo);
      window.removeEventListener('load', later);
      window.removeEventListener('resize', later);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [everyRoot]);
  return <span ref={mark} hidden />;
}
